import React, { useState } from "react";
import { CheckCircle, AlertTriangle, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { validarForcaSenha } from "../../../shared/utils/validarSenha";
import logo from "../../../assets/LOGO SEM FUNDO.PNG";
import {
  CategoriaProfissional,
  CATEGORIA_LABELS,
  UFS_BRASIL,
  categoriaRequerRegistro,
  getTipoRegistro,
} from "../../../types";

export default function CadastroUsuario() {
  // ============================================
  // 📊 ESTADOS DO FORMULÁRIO
  // ============================================
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [tipo, setTipo] = useState<"administrador" | "usuario" | "convidado">(
    "usuario"
  );

  // ⭐ NOVOS CAMPOS - CATEGORIA PROFISSIONAL
  const [categoria, setCategoria] = useState<CategoriaProfissional>("medico");
  const [registroNumero, setRegistroNumero] = useState("");
  const [registroUF, setRegistroUF] = useState("");

  // CAMPOS LEGADOS
  const [crm, setCrm] = useState("");
  const [especialidade, setEspecialidade] = useState("");

  // Estados de feedback
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState(false);
  const [carregando, setCarregando] = useState(false);

  const { cadastrarUsuario } = useAuth();

  // ============================================
  // 🔍 VALIDAÇÃO DE SENHA
  // ============================================
  const validarSenha = (senha: string) => validarForcaSenha(senha);

  // ============================================
  // 🎨 INDICADOR DE FORÇA DA SENHA
  // ============================================
  const calcularForcaSenha = (senha: string) => {
    let forca = 0;
    if (senha.length >= 8) forca++;
    if (senha.length >= 12) forca++;
    if (/[A-Z]/.test(senha)) forca++;
    if (/[0-9]/.test(senha)) forca++;
    if (/[^A-Za-z0-9]/.test(senha)) forca++;
    return forca;
  };

  const getForcaSenhaTexto = () => {
    const forca = calcularForcaSenha(senha);
    if (forca <= 1) return { texto: "Fraca", cor: "#ef4444" };
    if (forca <= 3) return { texto: "Média", cor: "#f59e0b" };
    return { texto: "Forte", cor: "#10b981" };
  };

  // ============================================
  // 📝 VALIDAÇÃO DE EMAIL
  // ============================================
  const validarEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // ============================================
  // ✅ SUBMIT DO FORMULÁRIO
  // ============================================
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro("");
    setSucesso(false);

    // 1️⃣ Validação de campos vazios
    if (!nome || !email || !senha) {
      setErro("Preencha todos os campos obrigatórios");
      return;
    }

    // 2️⃣ Validação de email
    if (!validarEmail(email)) {
      setErro("Email inválido");
      return;
    }

    // 3️⃣ Validação de senha
    const erroSenha = validarSenha(senha);
    if (erroSenha) {
      setErro(erroSenha);
      return;
    }

    // 4️⃣ Confirmação de senha
    if (senha !== confirmarSenha) {
      setErro("As senhas não coincidem");
      return;
    }

    // ⭐ 5️⃣ VALIDAÇÃO DE REGISTRO PROFISSIONAL
    if (categoriaRequerRegistro(categoria)) {
      if (!registroNumero.trim()) {
        const tipoRegistro = getTipoRegistro(categoria);
        setErro(`Número do ${tipoRegistro} é obrigatório`);
        return;
      }
      if (!registroUF) {
        setErro("UF é obrigatória");
        return;
      }
    }

    // 7️⃣ Tentar cadastrar
    setCarregando(true);
    try {
      const dadosCadastro: any = {
        nome,
        email,
        senha,
        tipo,
        categoria,
        especialidade: especialidade || undefined,
      };

      // ⭐ ADICIONAR REGISTRO PROFISSIONAL (se aplicável)
      if (categoriaRequerRegistro(categoria) && registroNumero.trim()) {
        const tipoRegistro = getTipoRegistro(categoria);
        dadosCadastro.registroProfissional = {
          numero: registroNumero.trim(),
          uf: registroUF,
          tipo: tipoRegistro!,
        };
      }

      const resultado = await cadastrarUsuario(dadosCadastro);

      if (resultado.success) {
        setSucesso(true);
        // Limpar formulário
        setNome("");
        setEmail("");
        setSenha("");
        setConfirmarSenha("");
        setCategoria("medico");
        setRegistroNumero("");
        setRegistroUF("");
        setCrm("");
        setEspecialidade("");
        setTipo("usuario");
      } else {
        setErro(resultado.message);
      }
    } catch (error) {
      setErro("Erro ao cadastrar usuário. Tente novamente.");
    } finally {
      setCarregando(false);
    }
  };

  // ========================================
  // VERIFICAR SE PRECISA MOSTRAR CAMPOS
  // ========================================
  const mostrarRegistro = categoriaRequerRegistro(categoria);
  const tipoRegistro = getTipoRegistro(categoria);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "rgba(30, 64, 175, 0.95)",
          backdropFilter: "blur(10px)",
          borderRadius: "24px",
          padding: "48px",
          width: "100%",
          maxWidth: "520px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        {/* LOGO */}
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <img
            src={logo}
            alt="Logo"
            style={{
              width: "100px",
              height: "100px",
              objectFit: "contain",
              borderRadius: "50%",
              background: "white",
              padding: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            }}
          />
          <h1
            style={{
              fontSize: "26px",
              fontWeight: "bold",
              color: "white",
              marginBottom: "8px",
              marginTop: "16px",
            }}
          >
            Cadastro de Usuário
          </h1>
          <p
            style={{
              fontSize: "14px",
              color: "#dbeafe",
            }}
          >
            Preencha os dados para criar sua conta
          </p>
        </div>

        {/* MENSAGEM DE SUCESSO */}
        {sucesso && (
          <div
            style={{
              background: "#d1fae5",
              border: "2px solid #10b981",
              color: "#064e3b",
              padding: "12px 16px",
              borderRadius: "12px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            <CheckCircle size={20} color="#16a34a" />
            <div>
              <div>Cadastro realizado com sucesso!</div>
              <div style={{ fontSize: "12px", marginTop: "4px", opacity: 0.8 }}>
                Agora você pode fazer login
              </div>
            </div>
          </div>
        )}

        {/* MENSAGEM DE ERRO */}
        {erro && (
          <div
            style={{
              background: "#fee2e2",
              border: "2px solid #ef4444",
              color: "#7f1d1d",
              padding: "12px 16px",
              borderRadius: "12px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            <AlertTriangle size={20} color="#ef4444" />
            <span>{erro}</span>
          </div>
        )}

        {/* FORMULÁRIO */}
        <form onSubmit={handleSubmit}>
          {/* NOME COMPLETO */}
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                color: "white",
                marginBottom: "8px",
              }}
            >
              Nome Completo *
            </label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Dr. João Silva"
              required
              disabled={carregando}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                borderRadius: "12px",
                fontSize: "15px",
                outline: "none",
                background: "rgba(255, 255, 255, 0.95)",
                boxSizing: "border-box",
                opacity: carregando ? 0.6 : 1,
              }}
            />
          </div>

          {/* EMAIL */}
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                color: "white",
                marginBottom: "8px",
              }}
            >
              Email *
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seuemail@exemplo.com"
              required
              disabled={carregando}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                borderRadius: "12px",
                fontSize: "15px",
                outline: "none",
                background: "rgba(255, 255, 255, 0.95)",
                boxSizing: "border-box",
                opacity: carregando ? 0.6 : 1,
              }}
            />
          </div>

          {/* TIPO DE USUÁRIO */}
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                color: "white",
                marginBottom: "8px",
              }}
            >
              Tipo de Usuário *
            </label>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value as any)}
              disabled={carregando}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                borderRadius: "12px",
                fontSize: "15px",
                outline: "none",
                background: "rgba(255, 255, 255, 0.95)",
                boxSizing: "border-box",
                opacity: carregando ? 0.6 : 1,
                cursor: "pointer",
              }}
            >
              <option value="usuario">Usuário</option>
              <option value="administrador">👑 Administrador</option>
              <option value="convidado">👁️ Convidado</option>
            </select>
          </div>

          {/* ⭐ CATEGORIA PROFISSIONAL */}
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                color: "white",
                marginBottom: "8px",
              }}
            >
              Categoria Profissional *
            </label>
            <select
              value={categoria}
              onChange={(e) => {
                setCategoria(e.target.value as CategoriaProfissional);
                // Limpar campos ao mudar categoria
                setRegistroNumero("");
                setRegistroUF("");
              }}
              disabled={carregando}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                borderRadius: "12px",
                fontSize: "15px",
                outline: "none",
                background: "rgba(255, 255, 255, 0.95)",
                boxSizing: "border-box",
                opacity: carregando ? 0.6 : 1,
                cursor: "pointer",
              }}
            >
              {(Object.keys(CATEGORIA_LABELS) as CategoriaProfissional[]).map(
                (cat) => (
                  <option key={cat} value={cat}>
                    {CATEGORIA_LABELS[cat]}
                  </option>
                )
              )}
            </select>
          </div>

          {/* ⭐ CAMPOS DINÂMICOS - REGISTRO PROFISSIONAL */}
          {mostrarRegistro && (
            <>
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "white",
                    marginBottom: "8px",
                  }}
                >
                  Número do {tipoRegistro} *
                </label>
                <input
                  type="text"
                  value={registroNumero}
                  onChange={(e) => setRegistroNumero(e.target.value)}
                  placeholder="123456"
                  required
                  disabled={carregando}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid rgba(255, 255, 255, 0.3)",
                    borderRadius: "12px",
                    fontSize: "15px",
                    outline: "none",
                    background: "rgba(255, 255, 255, 0.95)",
                    boxSizing: "border-box",
                    opacity: carregando ? 0.6 : 1,
                  }}
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "white",
                    marginBottom: "8px",
                  }}
                >
                  UF do {tipoRegistro} *
                </label>
                <select
                  value={registroUF}
                  onChange={(e) => setRegistroUF(e.target.value)}
                  required
                  disabled={carregando}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid rgba(255, 255, 255, 0.3)",
                    borderRadius: "12px",
                    fontSize: "15px",
                    outline: "none",
                    background: "rgba(255, 255, 255, 0.95)",
                    boxSizing: "border-box",
                    opacity: carregando ? 0.6 : 1,
                    cursor: "pointer",
                  }}
                >
                  <option value="">Selecione...</option>
                  {UFS_BRASIL.map((uf) => (
                    <option key={uf} value={uf}>
                      {uf}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* ESPECIALIDADE (OPCIONAL) */}
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                color: "white",
                marginBottom: "8px",
              }}
            >
              Especialidade / Área (Opcional)
            </label>
            <input
              type="text"
              value={especialidade}
              onChange={(e) => setEspecialidade(e.target.value)}
              placeholder={
                categoria === "medico"
                  ? "Ex: Hepatologia, Cirurgia Geral"
                  : "Ex: Área de especialização"
              }
              disabled={carregando}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                borderRadius: "12px",
                fontSize: "15px",
                outline: "none",
                background: "rgba(255, 255, 255, 0.95)",
                boxSizing: "border-box",
                opacity: carregando ? 0.6 : 1,
              }}
            />
          </div>

          {/* SENHA */}
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                color: "white",
                marginBottom: "8px",
              }}
            >
              Senha *
            </label>
            <input
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder="Mínimo 12 caracteres"
              required
              disabled={carregando}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                borderRadius: "12px",
                fontSize: "15px",
                outline: "none",
                background: "rgba(255, 255, 255, 0.95)",
                boxSizing: "border-box",
                opacity: carregando ? 0.6 : 1,
              }}
            />
            {/* INDICADOR DE FORÇA DA SENHA */}
            {senha && (
              <div style={{ marginTop: "8px", fontSize: "12px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      height: "6px",
                      background: "rgba(255,255,255,0.3)",
                      borderRadius: "3px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${(calcularForcaSenha(senha) / 5) * 100}%`,
                        height: "100%",
                        background: getForcaSenhaTexto().cor,
                        transition: "all 0.3s",
                      }}
                    />
                  </div>
                  <span
                    style={{
                      color: getForcaSenhaTexto().cor,
                      fontWeight: "600",
                    }}
                  >
                    {getForcaSenhaTexto().texto}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* CONFIRMAR SENHA */}
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                color: "white",
                marginBottom: "8px",
              }}
            >
              Confirmar Senha *
            </label>
            <input
              type="password"
              value={confirmarSenha}
              onChange={(e) => setConfirmarSenha(e.target.value)}
              placeholder="Digite a senha novamente"
              required
              disabled={carregando}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                borderRadius: "12px",
                fontSize: "15px",
                outline: "none",
                background: "rgba(255, 255, 255, 0.95)",
                boxSizing: "border-box",
                opacity: carregando ? 0.6 : 1,
              }}
            />
            {/* INDICADOR DE SENHA IGUAL */}
            {confirmarSenha && (
              <div
                style={{
                  marginTop: "8px",
                  fontSize: "12px",
                  color: senha === confirmarSenha ? "#10b981" : "#ef4444",
                  fontWeight: "600",
                }}
              >
                {senha === confirmarSenha
                  ? "✓ Senhas coincidem"
                  : "✗ Senhas não coincidem"}
              </div>
            )}
          </div>

          {/* BOTÃO CADASTRAR */}
          <button
            type="submit"
            disabled={carregando}
            style={{
              width: "100%",
              padding: "14px",
              background: carregando ? "#94a3b8" : "white",
              color: "#1e40af",
              border: "none",
              borderRadius: "12px",
              fontSize: "16px",
              fontWeight: "bold",
              cursor: carregando ? "not-allowed" : "pointer",
              transition: "all 0.3s",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
            }}
          >
            {carregando ? "Cadastrando..." : "Cadastrar"}
          </button>
        </form>

        {/* LINK PARA LOGIN */}
        <div
          style={{
            marginTop: "24px",
            paddingTop: "24px",
            borderTop: "1px solid rgba(255, 255, 255, 0.2)",
            textAlign: "center",
          }}
        >
          <p
            style={{
              fontSize: "14px",
              color: "#dbeafe",
              margin: 0,
            }}
          >
            Já tem uma conta?{" "}
            <span
              onClick={() => (window.location.href = "/")}
              style={{
                color: "white",
                fontWeight: "bold",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              Fazer Login
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
